import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Menu, X, Phone } from "lucide-react";
import { navLinks, services, business } from "../../mock/mock";

const Logo = ({ dark }) => (
  <Link to="/" className="flex flex-col leading-none select-none">
    <span
      className="font-display text-[22px] font-extrabold tracking-tight"
      style={{ color: dark ? "#0e0e10" : "#ffffff" }}
    >
      Talk <span style={{ color: "var(--tnf-blue)" }}>N'</span> Fix
    </span>
    <span
      className="text-[10px] italic font-medium tracking-wide -mt-0.5"
      style={{ color: dark ? "#7a7a82" : "rgba(255,255,255,0.75)" }}
    >
      Freehold
    </span>
  </Link>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const dark = scrolled; // dark text when scrolled (white bar), white text on transparent hero
  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className="fixed left-0 right-0 z-50 transition-all duration-300"
      style={{ top: "var(--ticker-h, 40px)" }}
    >
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? "bg-white/90 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
            : "bg-transparent"
        }`}
      >
        <div className="tnf-container flex items-center justify-between h-[74px]">
          <Logo dark={dark} />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.hasMenu ? (
                <div
                  key={link.to}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    onClick={() => navigate(link.to)}
                    className="flex items-center gap-1 px-4 py-2 text-[15px] font-medium rounded-full transition-colors"
                    style={{ color: dark ? "#0e0e10" : "#fff" }}
                  >
                    {link.label}
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${
                        servicesOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`absolute left-1/2 -translate-x-1/2 top-[calc(100%+8px)] w-[320px] origin-top transition-all duration-200 ${
                      servicesOpen
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-2 pointer-events-none"
                    }`}
                  >
                    <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.14)] border border-black/5 p-2">
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#f6f7f9] transition-colors group"
                        >
                          <img
                            src={s.image}
                            alt={s.title}
                            className="w-11 h-11 rounded-lg object-cover flex-shrink-0"
                          />
                          <div>
                            <div className="text-[14px] font-semibold text-[#0e0e10] group-hover:text-[var(--tnf-orange)] transition-colors">
                              {s.title}
                            </div>
                            <div className="text-[12px] text-[#7a7a82] line-clamp-1">
                              {s.short}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-4 py-2 text-[15px] font-medium rounded-full transition-colors relative"
                  style={{ color: dark ? "#0e0e10" : "#fff" }}
                >
                  {link.label}
                  {isActive(link.to) && (
                    <span
                      className="absolute left-4 right-4 -bottom-0.5 h-0.5 rounded-full"
                      style={{ background: "var(--tnf-orange)" }}
                    />
                  )}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/repair-estimate"
              className="tnf-pill hidden sm:inline-flex bg-white text-[#0e0e10] pl-5 pr-1.5 py-1.5 text-[14px] shadow-sm border border-black/5"
            >
              Book Repair Slot
              <span
                className="tnf-arrow w-8 h-8"
                style={{ background: "var(--tnf-blue)" }}
              >
                <ArrowUpRight size={17} color="#fff" />
              </span>
            </Link>

            <button
              className="lg:hidden p-2 rounded-lg"
              style={{ color: dark ? "#0e0e10" : "#fff" }}
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-[60] transition-all duration-300 ${
          mobileOpen ? "visible" : "invisible"
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 bottom-0 w-[84%] max-w-[360px] bg-white shadow-2xl transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between p-5 border-b border-black/5">
            <Logo dark />
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu">
              <X size={24} className="text-[#0e0e10]" />
            </button>
          </div>
          <div className="p-4 flex flex-col">
            <Link to="/" className="px-3 py-3 text-[16px] font-medium text-[#0e0e10] rounded-lg hover:bg-[#f6f7f9]">
              Home
            </Link>
            <div className="px-3 pt-3 pb-1 text-[12px] uppercase tracking-wider text-[#9a9aa2] font-semibold">
              Services
            </div>
            {services.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="px-3 py-2.5 text-[15px] text-[#3b3b3b] rounded-lg hover:bg-[#f6f7f9]"
              >
                {s.title}
              </Link>
            ))}
            <Link to="/pre-owned-devices" className="px-3 py-3 text-[16px] font-medium text-[#0e0e10] rounded-lg hover:bg-[#f6f7f9]">
              Pre-Owned Devices
            </Link>
            <Link to="/contact" className="px-3 py-3 text-[16px] font-medium text-[#0e0e10] rounded-lg hover:bg-[#f6f7f9]">
              Contact Us
            </Link>

            <Link
              to="/repair-estimate"
              className="tnf-pill justify-center mt-4 text-white pl-6 pr-2 py-2"
              style={{ background: "var(--tnf-orange)" }}
            >
              Book Repair Slot
              <span className="tnf-arrow w-8 h-8 bg-white">
                <ArrowUpRight size={17} color="var(--tnf-orange)" />
              </span>
            </Link>
            <a
              href={business.phoneRaw}
              className="flex items-center justify-center gap-2 mt-3 py-2.5 text-[15px] font-medium text-[#0e0e10] border border-black/10 rounded-full"
            >
              <Phone size={16} /> {business.phone}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
